import toastr from 'react-hot-toast';
import { useQuery, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import type {
  PluginConnectionsAttributes,
  SinglePluginConnectionsResponse,
} from 'types/PluginConnections';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useWorkspaceContext } from 'hooks/common';
import { NULL_CACHE_AND_STALE_TIME } from 'constants/index';

type mutationVariables = Partial<PluginConnectionsAttributes> & {
  pluginConnectionID: string | undefined;
};
type Props = {
  connectionConfigure: mutationVariables;
  enabled: boolean;
  onCreatePluginConnection: (pluginConnectionIDstring: string) => void;
};
export function useAutoPluginConnectionConfigure({
  connectionConfigure,
  enabled,
  onCreatePluginConnection,
}: Props) {
  const queryClient = useQueryClient();
  const { currentWorkspaceID } = useWorkspaceContext();

  const pluginConnectionConfigure = () => {
    const { pluginConnectionID, ...attributes } = connectionConfigure;

    const body = {
      data: {
        type: SPARK_RESOURCE_TYPES.PLUGIN_CONNECTIONS,
        ...(pluginConnectionID && { id: pluginConnectionID }),
        attributes: {
          ...attributes,
          workspaceID: currentWorkspaceID,
        },
      },
    };
    return fetchWrapper<SinglePluginConnectionsResponse>({
      url: SPARK_RESOURCE_TYPES.PLUGIN_CONNECTIONS_CONFIGURE,
      method: 'POST',
      body: body,
    });
  };

  const response = useQuery(
    ['AutoPluginConnectionConfigure', connectionConfigure.pluginConnectionID],
    pluginConnectionConfigure,
    {
      onSuccess: (data) => {
        onCreatePluginConnection(data.data.id);
        queryClient.invalidateQueries('pluginConnections');
        queryClient.invalidateQueries('pluginConnection');
      },
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `failed to configure Plugin Connection: ${error.message}`
          );
        }
      },
      enabled,
      ...NULL_CACHE_AND_STALE_TIME,
    }
  );

  return response;
}
